import { useState, useEffect, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Search, Pencil, Trash2, Inbox, ExternalLink, Link as LinkIcon } from "lucide-react";
import FormDialog from "@/components/FormDialog";
import { externeDokFields } from "@/lib/entities";

export default function ExterneDokSection({ kategorie, katLabel, testidPrefix }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/externe-dokumente", { params: { kategorie } });
      setRows(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Laden fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  }, [kategorie]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setValues({ label: "", link: "", kategorie }); setDialogOpen(true); };
  const openEdit = (r) => { setEditing(r); setValues({ label: r.label, link: r.link, kategorie }); setDialogOpen(true); };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { kategorie, label: values.label || "", link: values.link || "" };
      if (editing) {
        await api.put(`/externe-dokumente/${editing.id}`, payload);
        toast.success("Label aktualisiert");
      } else {
        await api.post("/externe-dokumente", payload);
        toast.success("Label erstellt");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Speichern fehlgeschlagen");
    } finally {
      setSubmitting(false);
    }
  };

  const doDelete = async () => {
    if (!confirm) return;
    try {
      await api.delete(`/externe-dokumente/${confirm.id}`);
      toast.success("Label gelöscht");
      load();
    } catch (e) {
      toast.error("Fehlgeschlagen");
    } finally {
      setConfirm(null);
    }
  };

  const filtered = rows.filter((r) => (!search.trim() ? true : (r.label || "").toLowerCase().includes(search.toLowerCase())));

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input data-testid={`${testidPrefix}-search`} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Suchen…" className="pl-9 bg-transparent border-zinc-700 rounded-sm focus-visible:ring-primary" />
        </div>
        <Button data-testid={`${testidPrefix}-create-btn`} onClick={openCreate} className="ml-auto rounded-sm bg-primary hover:bg-blue-600 text-white gap-2">
          <Plus className="h-4 w-4" /> Label erstellen
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-10 text-zinc-500 font-mono text-sm">Wird geladen…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-zinc-800 rounded-sm bg-[#0C0E12] py-14 flex flex-col items-center gap-2 text-zinc-600">
          <Inbox className="h-8 w-8" /><span className="font-mono text-sm">Keine Dokumente unter „{katLabel}“</span>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {filtered.map((r) => {
            const isLink = r.link && /^https?:\/\//i.test(r.link);
            return (
              <div key={r.id} data-testid={`${testidPrefix}-chip`} className="group inline-flex items-center gap-2 pl-3 pr-1.5 py-1.5 rounded-sm border border-zinc-800 bg-[#0C0E12] hover:border-primary/50 transition-colors duration-150">
                <LinkIcon className="h-3.5 w-3.5 text-primary shrink-0" />
                {isLink ? (
                  <a href={r.link} target="_blank" rel="noreferrer" className="text-sm text-zinc-100 hover:text-primary inline-flex items-center gap-1 max-w-[220px] truncate">
                    {r.label || "Dokument"} <ExternalLink className="h-3 w-3 shrink-0" />
                  </a>
                ) : (
                  <span className="text-sm text-zinc-300 max-w-[220px] truncate">{r.label || "—"}</span>
                )}
                <span className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                  <button data-testid={`${testidPrefix}-edit`} onClick={() => openEdit(r)} title="Bearbeiten" className="h-6 w-6 grid place-items-center rounded-sm text-zinc-500 hover:text-primary hover:bg-primary/10">
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button data-testid={`${testidPrefix}-delete`} onClick={() => setConfirm(r)} title="Löschen" className="h-6 w-6 grid place-items-center rounded-sm text-zinc-500 hover:text-red-400 hover:bg-red-500/10">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </span>
              </div>
            );
          })}
        </div>
      )}

      <FormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        title={editing ? "Label bearbeiten" : "Label erstellen"}
        fields={externeDokFields}
        values={values}
        setValues={setValues}
        onSubmit={submit}
        submitting={submitting}
        testidPrefix={testidPrefix}
      />

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="bg-[#0C0E12] border-zinc-800 rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-zinc-100">Label löschen?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">Dieser Vorgang kann nicht rückgängig gemacht werden.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm bg-transparent border-zinc-700 text-zinc-300">Abbrechen</AlertDialogCancel>
            <AlertDialogAction data-testid={`${testidPrefix}-confirm`} onClick={doDelete} className="rounded-sm bg-red-600 hover:bg-red-700 text-white">Bestätigen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
